import { API_BASE_URL } from "@kupikupi/brand";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelCartService,
  editCartService,
  generatePreOrderNo,
  queryPreOrder,
  uploadServiceImage,
} from "../endpoints/value-service";
import { useBackendLang } from "../lang-context";
import type { CancelServiceInput, EditServiceInput } from "../types/value-service";

const PREORDER_KEY = ["preorder"] as const;

const ctx = { baseUrl: API_BASE_URL };

/**
 * Drives the authoritative checkout summary. Given the exact picked cart-item
 * ids, it (1) mints a pre-order number, then (2) reads the server-computed
 * pre-order snapshot (item totals, domestic freight, value-add service fee,
 * grand total). All money comes from the backend — the client never recomputes
 * the value-add fee (iron rule "API 拿不到就不展示").
 *
 * `lang` is part of the key so a locale switch refetches localized titles. The
 * sorted-ids key means re-ticking the same items reuses the cache; changing the
 * selection (or invalidating after a service edit) re-mints a fresh pre-order.
 */
export function usePreOrder(authToken: string | null, cartItemIds: string[]) {
  const lang = useBackendLang();
  const idsKey = [...cartItemIds].sort().join(",");
  return useQuery({
    queryKey: [...PREORDER_KEY, lang, idsKey],
    queryFn: async () => {
      const preOrderNo = await generatePreOrderNo(ctx, authToken as string, cartItemIds);
      return queryPreOrder(ctx, authToken as string, preOrderNo, { lang });
    },
    enabled: !!authToken && cartItemIds.length > 0,
    staleTime: 0,
  });
}

/** Edit the value-added services on a cart item, then refresh the pre-order. */
export function useEditCartService(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: EditServiceInput) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return editCartService(ctx, authToken, input);
    },
    onSuccess: () => {
      // Only the pre-order carries the value-add fee + grand total shown on the
      // confirm-order page, so invalidating it is what refreshes the money. We
      // deliberately do NOT invalidate ["cart","list"]: a value-add edit never
      // changes cart membership/quantity/subtotal and the cart payload has no
      // value-add field, so that invalidate refreshed nothing relevant — but it
      // flipped useCart's isFetching, tripping the confirm-order guard
      // (`hasItemsParam && cartQ.isFetching → <Skeleton/>`) and white-flashing
      // the whole page on every photo/video toggle (#610).
      void qc.invalidateQueries({ queryKey: PREORDER_KEY });
    },
  });
}

/** Drop a whole value-add service category from a cart item. */
export function useCancelCartService(authToken: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CancelServiceInput) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return cancelCartService(ctx, authToken, input);
    },
    onSuccess: () => {
      // Same as useEditCartService: refresh only the pre-order, not the cart
      // list, so cancelling a service doesn't white-flash confirm-order (#610).
      void qc.invalidateQueries({ queryKey: PREORDER_KEY });
    },
  });
}

/** Upload a reference image for a custom-photo service; resolves to its URL. */
export function useUploadServiceImage(authToken: string | null) {
  return useMutation({
    mutationFn: async (file: File) => {
      if (!authToken) throw new Error("NO_TOKEN");
      return uploadServiceImage(ctx, authToken, file);
    },
  });
}
